import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { useGetCurrentUserSettings } from '../services/react-query/users/useGetCurrentUserSettings';
import { useUpdateUserSettings } from '../services/react-query/users/useUpdateUserSettings';

interface FilterDisplayContextType {
  showFilters: boolean | undefined;
  toggleFilters: () => void;
}

const FilterDisplayContext = createContext<FilterDisplayContextType | undefined>(undefined);

export const FilterDisplayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isMobile } = useIsMobile();
  const { data } = useGetCurrentUserSettings();
  const { mutate: updateUserSettings } = useUpdateUserSettings();
  const persistedShowFilters = data?.settings?.showFilters;
  const [showFilters, setShowFilters] = useState<boolean | undefined>(undefined);

  // Initialize state based on device + persisted settings
  useEffect(() => {
    // Mobile → always start closed
    if (isMobile) {
      setShowFilters(false);
      return;
    }

    // Desktop → wait for settings
    if (persistedShowFilters !== undefined) {
      setShowFilters(persistedShowFilters);
    } else {
      setShowFilters(true); // default desktop
    }
  }, [isMobile, persistedShowFilters]);

  // Toggle handler
  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => {
      if (prev === undefined) return prev;

      const next = !prev;

      // Only persist on desktop
      if (!isMobile && persistedShowFilters !== next) {
        updateUserSettings({ showFilters: next });
      }

      return next;
    });
  }, [isMobile, persistedShowFilters, updateUserSettings]);

  return (
    <FilterDisplayContext.Provider
      value={{
        showFilters,
        toggleFilters,
      }}
    >
      {children}
    </FilterDisplayContext.Provider>
  );
};

export const useFilterDisplay = () => {
  const context = useContext(FilterDisplayContext);

  if (!context) {
    throw new Error('useFilterDisplay must be used within FilterDisplayProvider');
  }

  return context;
};
